import {
  DataTableBlock,
  type DataTableColumn,
} from "@sylviq/ui-react-web/blocks/table";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { type FormEvent, useEffect, useState } from "react";
import {
  createLegalEntity,
  deleteLegalEntity,
  type LegalEntity,
  listLegalEntities,
  listOrganizations,
} from "@/lib/api";
import { useSession } from "@/lib/session";
import {
  OrganizationNavigation,
  organizationViewHref,
  type ConsoleOrganizationOption,
} from "@/features/organizations/OrganizationNavigation";

type OrganizationOption = ConsoleOrganizationOption;

type PageState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "loaded";
      organizationId: string;
      organizationName: string;
      organizations: OrganizationOption[];
      entities: LegalEntity[];
    };

type EntityForm = {
  kind: "person" | "organization";
  name: string;
  slug: string;
  taxClassification: string;
  taxId: string;
  registrationNumber: string;
  countryCode: string;
  addressLine1: string;
  addressCity: string;
  addressRegion: string;
  addressPostalCode: string;
};

const EMPTY_FORM: EntityForm = {
  kind: "organization",
  name: "",
  slug: "",
  taxClassification: "",
  taxId: "",
  registrationNumber: "",
  countryCode: "",
  addressLine1: "",
  addressCity: "",
  addressRegion: "",
  addressPostalCode: "",
};

function slugify(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function kindLabel(kind: string): string {
  return kind === "person" ? "Person" : "Organization";
}

function addressLabel(entity: LegalEntity): string {
  return [
    entity.address_line1,
    entity.address_city,
    entity.address_region,
    entity.address_postal_code,
    entity.country_code,
  ]
    .filter((part) => Boolean(part))
    .join(", ") || "—";
}

const ENTITY_COLUMNS: readonly DataTableColumn<LegalEntity>[] = [
  {
    key: "name",
    header: "Name",
    cell: (entity) => (
      <>
        <div className="font-medium">{entity.name}</div>
        <div className="text-xs text-[var(--muted-foreground)]">
          {entity.slug}
        </div>
      </>
    ),
  },
  {
    key: "kind",
    header: "Kind",
    cell: (entity) => kindLabel(entity.kind),
  },
  {
    key: "taxId",
    header: "Tax ID",
    cell: (entity) =>
      entity.tax_id_last4 ? `•••• ${entity.tax_id_last4}` : "—",
  },
  {
    key: "address",
    header: "Address",
    cell: (entity) => addressLabel(entity),
  },
  {
    key: "status",
    header: "Status",
    cell: (entity) => (
      <Badge tone={entity.active ? "success" : "neutral"}>
        {entity.active ? "Active" : "Inactive"}
      </Badge>
    ),
  },
];

export function OrganizationEntitiesPage({
  organizationId,
}: {
  organizationId: string;
}) {
  const { state } = useSession();
  const userId = state.status === "authenticated" ? state.user.id : null;
  const [page, setPage] = useState<PageState>({ status: "loading" });
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<EntityForm>(EMPTY_FORM);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LegalEntity | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedEntityIds, setSelectedEntityIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    let cancelled = false;
    setPage({ status: "loading" });
    setNotice(null);
    void (async () => {
      try {
        const organizations = await listOrganizations();
        if (cancelled) return;
        const organization = organizations.find(
          (candidate) => candidate.id === organizationId,
        );
        if (!organization) {
          setPage({
            status: "error",
            message: "You do not have access to this organization.",
          });
          return;
        }
        const entities = await listLegalEntities(organization.id);
        if (!cancelled) {
          setPage({
            status: "loaded",
            organizationId: organization.id,
            organizationName: organization.name,
            organizations: organizations.map((item) => ({
              id: item.id,
              name: item.name,
              canManageSecrets: item.can_manage_secrets,
            })),
            entities,
          });
        }
      } catch {
        if (!cancelled) {
          setPage({
            status: "error",
            message:
              "This organization's legal entities could not be loaded. Please try again.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, state.status, userId]);

  function updateName(name: string) {
    setForm((current) => {
      const slugWasAutomatic =
        current.slug === "" || current.slug === slugify(current.name);
      return {
        ...current,
        name,
        slug: slugWasAutomatic ? slugify(name) : current.slug,
      };
    });
  }

  function closeCreate() {
    setCreateOpen(false);
    setForm(EMPTY_FORM);
    setCreateError(null);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (page.status !== "loaded" || createBusy) return;
    setCreateBusy(true);
    setCreateError(null);
    const name = form.name.trim();
    try {
      await createLegalEntity({
        organization_id: page.organizationId,
        kind: form.kind,
        name,
        slug: form.slug.trim(),
        tax_classification: optional(form.taxClassification),
        tax_id: optional(form.taxId),
        registration_number: optional(form.registrationNumber),
        country_code: optional(form.countryCode),
        address_line1: optional(form.addressLine1),
        address_city: optional(form.addressCity),
        address_region: optional(form.addressRegion),
        address_postal_code: optional(form.addressPostalCode),
      });
      closeCreate();
      setNotice(`${name} was added.`);
      try {
        const entities = await listLegalEntities(page.organizationId);
        setPage({ ...page, entities });
      } catch {
        setNotice(
          `${name} was added. The list could not refresh; reload to see it.`,
        );
      }
    } catch (cause) {
      setCreateError(
        cause instanceof Error ? cause.message : String(cause),
      );
    } finally {
      setCreateBusy(false);
    }
  }

  function openDelete(entity: LegalEntity) {
    setDeleteTarget(entity);
    setDeleteConfirmation("");
    setDeleteError(null);
  }

  function closeDelete() {
    if (deleteBusy) return;
    setDeleteTarget(null);
    setDeleteConfirmation("");
    setDeleteError(null);
  }

  async function confirmDelete() {
    if (page.status !== "loaded" || !deleteTarget || deleteBusy) return;
    if (deleteConfirmation !== deleteTarget.slug) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteLegalEntity({
        organization_id: page.organizationId,
        legal_entity_id: deleteTarget.id,
      });
      const name = deleteTarget.name;
      setPage({
        ...page,
        entities: page.entities.filter((entity) => entity.id !== deleteTarget.id),
      });
      setDeleteTarget(null);
      setDeleteConfirmation("");
      setNotice(`${name} and its books data were deleted.`);
    } catch (cause) {
      setDeleteError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setDeleteBusy(false);
    }
  }

  if (page.status === "loading") {
    return (
      <main className="mx-auto max-w-5xl px-6 py-10 text-sm text-[var(--muted-foreground)]">
        Loading legal entities...
      </main>
    );
  }

  if (page.status === "error") {
    return (
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p role="alert" className="text-sm text-[var(--danger)]">
          {page.message}
        </p>
      </main>
    );
  }

  return (
    <OrganizationNavigation
      organizationId={page.organizationId}
      organizations={page.organizations}
      onOrganizationChange={(nextOrganizationId) => {
        if (nextOrganizationId === page.organizationId) return;
        window.location.assign(
          organizationViewHref(nextOrganizationId, "entities"),
        );
      }}
      view="entities"
    >
      <div className="space-y-8">
        <PageHeader
          title="Legal entities"
          description={
            page.organizationName
              ? `${page.organizationName} · The people and organizations that can keep books in this organization.`
              : "The people and organizations that can keep books in this organization."
          }
          actions={
            <Button onClick={() => setCreateOpen(true)} type="button">
              Add legal entity
            </Button>
          }
        />

        {notice ? (
          <p role="status" className="text-sm text-[var(--muted-foreground)]">
            {notice}
          </p>
        ) : null}

        <DataTableBlock
          actionsHeader="Actions"
          columns={ENTITY_COLUMNS}
          emptyState="No legal entities yet. Add the first person or organization that keeps books in this organization."
          getRowId={(entity) => entity.id}
          getRowLabel={(entity) => entity.name}
          onSelectedIdsChange={setSelectedEntityIds}
          rowActions={(entity) => [
            {
              id: `delete-${entity.id}`,
              label: "Delete",
              ariaLabel: `Delete ${entity.name}`,
              disabled: entity.is_primary === true,
              title: entity.is_primary
                ? "The primary legal entity cannot be deleted."
                : undefined,
              onSelect: () => openDelete(entity),
            },
          ]}
          rowActionsAriaLabel={(entity) => `Actions for ${entity.name}`}
          rowActionsLabel="Actions"
          rows={page.entities}
          selectable={false}
          selectedIds={selectedEntityIds}
          tableLabel="Legal entities"
          testId="organization-legal-entities"
        />

        <Dialog
          open={createOpen}
          onOpenChange={(open) => {
            if (open) setCreateOpen(true);
            else closeCreate();
          }}
          title="Add legal entity"
          description="Create the person or organization that owns a set of books. Accounting switches the active entity in its own header."
        >
          <form className="grid gap-4" onSubmit={(event) => void submit(event)}>
            <label className="grid gap-1 text-sm font-medium">
              Kind
              <Select
                aria-label="Legal entity kind"
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    kind: value as EntityForm["kind"],
                  }))
                }
                options={[
                  { value: "organization", label: "Organization" },
                  { value: "person", label: "Person" },
                ]}
                value={form.kind}
              />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1 text-sm font-medium">
                {form.kind === "person" ? "Full name" : "Legal name"}
                <Input
                  id="legal-entity-name"
                  onChange={(event) => updateName(event.target.value)}
                  required
                  value={form.name}
                />
              </label>
              <label className="grid gap-1 text-sm font-medium">
                Slug
                <Input
                  id="legal-entity-slug"
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      slug: event.target.value,
                    }))
                  }
                  pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                  required
                  value={form.slug}
                />
              </label>
              <label className="grid gap-1 text-sm font-medium">
                Tax classification
                <Input
                  id="legal-entity-tax-classification"
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      taxClassification: event.target.value,
                    }))
                  }
                  placeholder="LLC, S corporation, individual"
                  value={form.taxClassification}
                />
              </label>
              <label className="grid gap-1 text-sm font-medium">
                Tax ID
                <Input
                  id="legal-entity-tax-id"
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      taxId: event.target.value,
                    }))
                  }
                  value={form.taxId}
                />
                <span className="text-xs font-normal text-[var(--muted-foreground)]">
                  Write-only. Only the last four digits are shown later.
                </span>
              </label>
              <label className="grid gap-1 text-sm font-medium">
                Registration number
                <Input
                  id="legal-entity-registration-number"
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      registrationNumber: event.target.value,
                    }))
                  }
                  value={form.registrationNumber}
                />
              </label>
              <label className="grid gap-1 text-sm font-medium">
                Country code
                <Input
                  id="legal-entity-country-code"
                  maxLength={2}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      countryCode: event.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="US"
                  value={form.countryCode}
                />
              </label>
            </div>
            <label className="grid gap-1 text-sm font-medium">
              Address
              <Input
                id="legal-entity-address-line1"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    addressLine1: event.target.value,
                  }))
                }
                value={form.addressLine1}
              />
            </label>
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="grid gap-1 text-sm font-medium">
                City
                <Input
                  id="legal-entity-address-city"
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      addressCity: event.target.value,
                    }))
                  }
                  value={form.addressCity}
                />
              </label>
              <label className="grid gap-1 text-sm font-medium">
                Region
                <Input
                  id="legal-entity-address-region"
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      addressRegion: event.target.value,
                    }))
                  }
                  value={form.addressRegion}
                />
              </label>
              <label className="grid gap-1 text-sm font-medium">
                Postal code
                <Input
                  id="legal-entity-address-postal-code"
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      addressPostalCode: event.target.value,
                    }))
                  }
                  value={form.addressPostalCode}
                />
              </label>
            </div>
            {createError ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {createError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                disabled={createBusy}
                onClick={closeCreate}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button disabled={createBusy} type="submit">
                {createBusy ? "Creating..." : "Create legal entity"}
              </Button>
            </div>
          </form>
        </Dialog>

        <Dialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) closeDelete();
          }}
          title="Delete legal entity"
          description="This permanently removes the entity and every row in its books. It cannot be undone."
        >
          <div className="grid gap-4">
            <p className="text-sm text-[var(--muted-foreground)]">
              {deleteTarget
                ? `Delete ${deleteTarget.name} and its chart of accounts, journals, invoices, bills, bank data, payroll, budgets, assets, loans, tax rates, licenses, and locations? Other legal entities are not touched.`
                : null}
            </p>
            <label className="grid gap-1 text-sm font-medium">
              {deleteTarget
                ? `Type ${deleteTarget.slug} to confirm`
                : "Type the slug to confirm"}
              <Input
                id="delete-legal-entity-confirmation"
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                value={deleteConfirmation}
              />
            </label>
            {deleteError ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {deleteError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                disabled={deleteBusy}
                onClick={closeDelete}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={
                  deleteBusy ||
                  !deleteTarget ||
                  deleteConfirmation !== deleteTarget.slug
                }
                onClick={() => void confirmDelete()}
                type="button"
                variant="destructive"
              >
                {deleteBusy ? "Deleting..." : "Delete permanently"}
              </Button>
            </div>
          </div>
        </Dialog>
      </div>
    </OrganizationNavigation>
  );
}
